/**
 * Derives hero pastel + accent colours from the product image.
 *
 * Sets `--dimark-accent-pastel` for the hero background (soft wash of the
 * product hue) and `--dimark-accent` for buttons/progress (vivid, readable).
 * Falls back to Liquid-rendered values when extraction fails.
 */
(() => {
  const SAMPLE = 64;
  /* Bumped to v2 when cut-out detection was added, so sessions holding the
     older cache shape re-measure instead of reading a missing flag. */
  const storagePrefix = 'dimark-accent-v2:';

  /* Vivid accent — buttons, progress bar, CTAs */
  const MIN_SAT = 0.55;
  const LIGHT_SHIFT = 0.2;
  const LIGHT_HI = 0.58;
  const LIGHT_LO = 0.42;
  const TITLE_CONTRAST = 3;

  /* Pastel hero — keep hue, low saturation, high lightness */
  const PASTEL_SAT_MIN = 0.2;
  const PASTEL_SAT_MAX = 0.42;
  const PASTEL_SAT_SCALE = 0.5;
  const PASTEL_LIGHT = 0.91;
  const PASTEL_LIGHT_DARK = 0.74;

  const relLuminance = ([r, g, b]) => {
    const f = (c) => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  };

  const contrastWithWhite = (rgb) => 1.05 / (relLuminance(rgb) + 0.05);

  const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return [h, s, l];
  };

  const hslToRgb = (h, s, l) => {
    if (s === 0) {
      const v = Math.round(l * 255);
      return [v, v, v];
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const conv = (t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    return [
      Math.round(conv(h + 1 / 3) * 255),
      Math.round(conv(h) * 255),
      Math.round(conv(h - 1 / 3) * 255)
    ];
  };

  /**
   * @param {HTMLImageElement} img
   * @returns {{ accent: string, pastel: string, pastelDark: string }|null}
   */
  const extractColors = (img) => {
    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE;
    canvas.height = SAMPLE;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);

    const { data } = ctx.getImageData(0, 0, SAMPLE, SAMPLE);

    /* Is this a cut-out (transparent background) or a photo on a solid plate?
       drop-shadow() works off the alpha channel, so on a solid-background image
       it outlines the picture's rectangle instead of the product - which looks
       like a misplaced box. Reading the border ring answers it: a cut-out has
       transparent edges, a plated photo does not. Done here rather than in a
       second pass so it costs no extra decode. */
    const alphaAt = (x, y) => data[(y * SAMPLE + x) * 4 + 3];
    let borderPixels = 0;
    let borderTransparent = 0;

    for (let i = 0; i < SAMPLE; i += 1) {
      [[i, 0], [i, SAMPLE - 1], [0, i], [SAMPLE - 1, i]].forEach(([x, y]) => {
        borderPixels += 1;
        if (alphaAt(x, y) < 32) borderTransparent += 1;
      });
    }

    const cutout = borderPixels > 0 && borderTransparent / borderPixels > 0.6;

    const buckets = new Map();

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      if (a < 200) continue;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);

      if (max > 240 && min > 240) continue;
      if (max < 20) continue;
      if (max - min < 24) continue;

      const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = { r: 0, g: 0, b: 0, n: 0 };
        buckets.set(key, bucket);
      }
      bucket.r += r;
      bucket.g += g;
      bucket.b += b;
      bucket.n += 1;
    }

    let best = null;
    buckets.forEach((v) => {
      if (!best || v.n > best.n) best = v;
    });

    if (!best) return null;

    const [h, s, l] = rgbToHsl(best.r / best.n, best.g / best.n, best.b / best.n);

    const pastelSat = Math.min(
      PASTEL_SAT_MAX,
      Math.max(PASTEL_SAT_MIN, s * PASTEL_SAT_SCALE)
    );
    const pastelRgb = hslToRgb(h, pastelSat, PASTEL_LIGHT);
    const pastelDarkRgb = hslToRgb(
      h,
      Math.min(PASTEL_SAT_MAX, pastelSat * 1.12),
      PASTEL_LIGHT_DARK
    );

    let light = l < 0.5
      ? Math.min(l + LIGHT_SHIFT, LIGHT_HI)
      : Math.max(l - LIGHT_SHIFT, LIGHT_LO);

    const sat = Math.max(s, MIN_SAT);

    let accentRgb = hslToRgb(h, sat, light);
    while (contrastWithWhite(accentRgb) < TITLE_CONTRAST && light > 0.18) {
      light -= 0.02;
      accentRgb = hslToRgb(h, sat, light);
    }

    return {
      pastel: `${pastelRgb[0]}, ${pastelRgb[1]}, ${pastelRgb[2]}`,
      pastelDark: `${pastelDarkRgb[0]}, ${pastelDarkRgb[1]}, ${pastelDarkRgb[2]}`,
      accent: `${accentRgb[0]}, ${accentRgb[1]}, ${accentRgb[2]}`,
      cutout
    };
  };

  const markColorsReady = (target) => {
    target.classList.add('dimark-colors-ready');
  };

  const applyColors = (target, colors, src) => {
    if (!target || !colors) return;

    target.style.setProperty('--dimark-accent-pastel-dark', colors.pastelDark);
    target.style.setProperty('--dimark-accent-pastel', colors.pastel);
    target.style.setProperty('--dimark-accent', colors.accent);

    /* The contour shadow only reads correctly on a cut-out. Gate it here rather
       than in Liquid, since only the pixels can tell us. */
    target.classList.toggle('dimark-has-cutout', colors.cutout === true);

    markColorsReady(target);

    if (!src) return;

    try {
      sessionStorage.setItem(storagePrefix + src, JSON.stringify(colors));
    } catch (e) {
      // Private browsing or quota — ignore.
    }
  };

  const readCachedColors = (src) => {
    if (!src) return null;

    try {
      const raw = sessionStorage.getItem(storagePrefix + src);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  const applyAccent = (hero) => {
    const src = hero.dataset.dimarkColorSource;
    if (!src) return;

    const target = hero.closest('.shopify-section') || hero;
    if (target.classList.contains('dimark-colors-ready')) return;

    const cached = readCachedColors(src);
    if (cached) {
      applyColors(target, cached, src);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    const finish = (colors) => {
      if (colors) {
        applyColors(target, colors, src);
      } else {
        markColorsReady(target);
      }
    };

    img.addEventListener('load', () => {
      try {
        finish(extractColors(img));
      } catch (e) {
        finish(null);
      }
    });

    img.addEventListener('error', () => {
      finish(null);
    });

    img.src = src;
  };

  const init = () => {
    try {
      document.querySelectorAll('.dimark-hero[data-dimark-color-source]').forEach(applyAccent);
    } catch (e) {
      // Never let a colour tweak break the rest of the page.
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
